import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TradeLogsTable = ({ trades }) => {
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const filteredTrades = trades?.filter(trade => {
    if (filterType === 'all') return true;
    if (filterType === 'profitable') return trade?.pnl > 0;
    if (filterType === 'losses') return trade?.pnl < 0;
    return true;
  });

  const sortedTrades = [...filteredTrades]?.sort((a, b) => {
    let aValue = a?.[sortField];
    let bValue = b?.[sortField];
    
    if (sortField === 'date') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const totalPages = Math.ceil(sortedTrades?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTrades = sortedTrades?.slice(startIndex, startIndex + itemsPerPage);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />;
    return (
      <Icon 
        name={sortDirection === 'asc' ? "ArrowUp" : "ArrowDown"} 
        size={14} 
        className="text-primary" 
      />
    );
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-8">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-foreground mb-2">Trade Logs</h2>
          <p className="text-sm text-muted-foreground">Detailed history of all executed trades</p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 mt-4 lg:mt-0">
          {/* Filter Buttons */}
          <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
            {[
              { label: 'All', value: 'all' },
              { label: 'Profitable', value: 'profitable' },
              { label: 'Losses', value: 'losses' }
            ]?.map((filter) => (
              <button
                key={filter?.value}
                onClick={() => setFilterType(filter?.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md trading-transition ${
                  filterType === filter?.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {filter?.label}
              </button>
            ))}
          </div>

          <Button variant="ghost" size="sm" iconName="Download">
            Export Trades
          </Button>
        </div>
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-secondary">
              <th className="text-left py-3 px-4">
                <button
                  onClick={() => handleSort('date')}
                  className="flex items-center space-x-2 text-sm font-medium text-muted-foreground hover:text-foreground trading-transition"
                >
                  <span>Date</span>
                  <SortIcon field="date" />
                </button>
              </th>
              <th className="text-left py-3 px-4">
                <button
                  onClick={() => handleSort('pair')}
                  className="flex items-center space-x-2 text-sm font-medium text-muted-foreground hover:text-foreground trading-transition"
                >
                  <span>Pair</span>
                  <SortIcon field="pair" />
                </button>
              </th>
              <th className="text-left py-3 px-4">
                <span className="text-sm font-medium text-muted-foreground">Side</span>
              </th>
              <th className="text-right py-3 px-4">
                <button
                  onClick={() => handleSort('entryPrice')}
                  className="flex items-center space-x-2 text-sm font-medium text-muted-foreground hover:text-foreground trading-transition ml-auto"
                >
                  <span>Entry</span>
                  <SortIcon field="entryPrice" />
                </button>
              </th>
              <th className="text-right py-3 px-4">
                <button
                  onClick={() => handleSort('exitPrice')}
                  className="flex items-center space-x-2 text-sm font-medium text-muted-foreground hover:text-foreground trading-transition ml-auto"
                >
                  <span>Exit</span>
                  <SortIcon field="exitPrice" />
                </button>
              </th>
              <th className="text-right py-3 px-4">
                <button
                  onClick={() => handleSort('quantity')}
                  className="flex items-center space-x-2 text-sm font-medium text-muted-foreground hover:text-foreground trading-transition ml-auto"
                >
                  <span>Quantity</span>
                  <SortIcon field="quantity" />
                </button>
              </th>
              <th className="text-right py-3 px-4">
                <span className="text-sm font-medium text-muted-foreground">Duration</span>
              </th>
              <th className="text-right py-3 px-4">
                <button
                  onClick={() => handleSort('pnl')}
                  className="flex items-center space-x-2 text-sm font-medium text-muted-foreground hover:text-foreground trading-transition ml-auto"
                >
                  <span>P&L</span>
                  <SortIcon field="pnl" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedTrades?.map((trade) => (
              <tr key={trade?.id} className="border-b border-border-secondary hover:bg-muted/50 trading-transition">
                <td className="py-4 px-4">
                  <div className="text-sm text-foreground">{trade?.date}</div>
                  <div className="text-xs text-muted-foreground">{trade?.time}</div>
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-primary">{trade?.pair?.split('/')?.[0]?.slice(0, 2)}</span>
                    </div>
                    <span className="text-sm font-medium text-foreground">{trade?.pair}</span>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    trade?.side === 'BUY' ?'bg-success/10 text-success' :'bg-error/10 text-error'
                  }`}>
                    {trade?.side}
                  </span>
                </td>
                <td className="py-4 px-4 text-right">
                  <span className="text-sm font-medium text-foreground">${trade?.entryPrice?.toLocaleString()}</span>
                </td>
                <td className="py-4 px-4 text-right">
                  <span className="text-sm font-medium text-foreground">${trade?.exitPrice?.toLocaleString()}</span>
                </td>
                <td className="py-4 px-4 text-right">
                  <span className="text-sm font-medium text-foreground">{trade?.quantity}</span>
                </td>
                <td className="py-4 px-4 text-right">
                  <span className="text-sm text-muted-foreground">{formatDuration(trade?.duration)}</span>
                </td>
                <td className="py-4 px-4 text-right">
                  <div className="text-right">
                    <div className={`text-sm font-medium ${
                      trade?.pnl >= 0 ? 'text-success' : 'text-error'
                    }`}>
                      {trade?.pnl >= 0 ? '+' : ''}${trade?.pnl?.toLocaleString()}
                    </div>
                    <div className={`text-xs ${
                      trade?.pnlPercent >= 0 ? 'text-success' : 'text-error'
                    }`}>
                      {trade?.pnlPercent >= 0 ? '+' : ''}{trade?.pnlPercent}%
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-border-secondary">
          <div className="text-sm text-muted-foreground">
            Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredTrades?.length)} of {filteredTrades?.length} trades
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              iconName="ChevronLeft"
            >
              Previous
            </Button>
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 text-sm font-medium rounded-md trading-transition ${
                      currentPage === page
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              iconName="ChevronRight"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeLogsTable;